'use client';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import FadeInSection from './FadeInSection';
import dynamic from 'next/dynamic';

const ImageComparisonSlider = dynamic(() => import('./ImageComparisonSlider'), { ssr: false });

/* ---- GA helper (local only) ---- */
function ga(event: string, params?: Record<string, unknown>) {
  try {
    if (typeof window === 'undefined') return;
    if (window.top !== window.self) return; // parent-only iframes: keep this
    const w: any = window;
    if (Array.isArray(w.dataLayer)) {
      w.dataLayer.push({ event, ...(params || {}) });
      return;
    }
    // Fallback if gtag is still present in older deploys
    if (typeof w.gtag === 'function') {
      w.gtag('event', event, params || {});
    }
  } catch {}
}


/* ---- WP copy endpoint ---- */
const WP_MARKETING_ENDPOINT = 'https://wateredgetech.com/wp-json/wet/v1/marketing';

type BlockId = 'soap_scum' | 'laundry' | 'skin_hair' | 'appliances';

// Updated type to include the list of bullet points
type BlockCopy = {
  id: BlockId;
  title: string;
  body: string;
  list: string[];
};

// Updated defaults to include the list items
const DEFAULT_BLOCKS: Record<BlockId, BlockCopy> = {
  soap_scum: {
    id: 'soap_scum',
    title: 'Soap Scum Buildup',
    body:
      'Tired of scrubbing? Our system eliminates the minerals that cause stubborn chalky buildup, making surfaces sparkle effortlessly.',
    list: [
      'Faster Cleaning: Wipe-offs replace heavy scrubbing.',
      'Less Chemistry: Cut back on harsh cleaners.',
      'Clearer Glass Longer: Slower film re-deposit.',
      'Time Saved: Shorter bathroom cleanups.',
    ],
  },
  laundry: {
    id: 'laundry',
    title: 'Dull, Stiff Laundry',
    body:
      'Hard water makes fabrics feel rough and look faded. Experience noticeably softer, brighter laundry with soft water.',
    list: [
      'Softer Hand: Restored loft in towels and tees.',
      'Truer Color: Reduced graying and fading.',
      'Detergent Savings: Clean with less soap.',
      'Fewer Rinses: Shorter wash cycles.',
    ],
  },
  skin_hair: {
    id: 'skin_hair',
    title: 'Dry Skin & Hair',
    body:
      'Say goodbye to dry, itchy skin and brittle hair. Soft water leaves you feeling refreshed and moisturized.',
    list: [
      'Cleaner Rinse: Less residue left on skin.',
      'Smoother Hair: Easier comb-through and less frizz.',
      'Milder Products: Use gentler shampoos and lotions.',
      'Comfort Boost: Reduced itch and tightness.',
    ],
  },
  appliances: {
    id: 'appliances',
    title: 'Appliance Damage',
    body:
      'Protect your investments! Hard water minerals cause costly limescale buildup, shortening the life of your appliances.',
    list: [
      'Longer Lifespan: Less wear on elements and valves.',
      'Energy Efficiency: Better heat transfer and flow.',
      'Fewer Service Calls: Lower scale-related failures.',
      'Stable Performance: Consistent water pressure and output.',
    ],
  },
};

const WhySoftWater: React.FC = () => {
  // WP overrides for heading + sub
  const [heading, setHeading] = useState('Soft water systems that fix hard water in San Antonio');
  const [sub, setSub] = useState(
    'Hard water minerals cause buildup, dry skin, and appliance wear. A Water Edge Technologies water softener removes the minerals and delivers whole-home soft water.'
  );

  // Per-block copy (override from WP if provided)
  const [blocks, setBlocks] = useState<Record<BlockId, BlockCopy>>(DEFAULT_BLOCKS);

  useEffect(() => {
    let cancelled = false;
    const ctrl = new AbortController();
    (async () => {
      try {
        const r = await fetch(WP_MARKETING_ENDPOINT, {
          headers: { Accept: 'application/json' },
          cache: 'no-store',
          signal: ctrl.signal,
        });
        if (!r.ok) return;

        const data = await r.json();
        if (cancelled) return;

        const sec = data?.sections || {};
        if (typeof sec.why_heading === 'string' && sec.why_heading.trim()) setHeading(sec.why_heading.trim());
        if (typeof sec.why_sub === 'string' && sec.why_sub.trim()) setSub(sec.why_sub.trim());
        
        // Updated logic to also check for a 'list' property in the WP data
        const arr = Array.isArray(sec.why_blocks) ? sec.why_blocks : [];
        if (arr.length) {
          setBlocks((prev) => {
            const next = { ...prev };
            for (const item of arr) {
              const id = (item?.id || '').trim() as BlockId;
              if (!id || !(id in prev)) continue;
              
              const isValidList = Array.isArray(item?.list) && item.list.every((i: any) => typeof i === 'string');

              next[id] = {
                id,
                title: (item?.title || prev[id].title).trim(),
                body: (item?.body || prev[id].body).trim(),
                list: isValidList ? item.list.map((i: string) => i.trim()).filter(Boolean) : prev[id].list,
              };
            }
            return next;
          });
        }
      } catch {
        /* keep defaults */
      }
    })();
    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, []);

  // Section view once
  const sectionRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    let fired = false;
    const io = new IntersectionObserver(
      (entries) => {
        if (fired) return;
        if (entries.some((e) => e.isIntersecting)) {
          fired = true;
          ga('section_view', { section: 'why_soft_water' });
          io.disconnect();
        }
      },
      { threshold: 0.35 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Per-block view tracking
  const mkBlockIO = () => {
    const sent = new Set<string>();
    return (node: Element | null, id: string) => {
      if (!node) return;
      const obs = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting && !sent.has(id)) {
              sent.add(id);
              ga('compare_view', { section: 'why_soft_water', item_id: id });
            }
          });
        },
        { threshold: 0.4 }
      );
      obs.observe(node);
    };
  };
  const observeBlock = useMemo(mkBlockIO, []);
  
  // Helper to render list items and split the text for bolding
  const renderListItem = (item: string, index: number) => {
      const parts = item.split(':');
      const strongText = parts.length > 1 ? `${parts.shift()}:` : null;
      const restText = parts.join(':').trim();
      return (
        <li key={index}>
          {strongText && <strong>{strongText}</strong>} {restText}
        </li>
      )
  }

  return (
    <section
      id="why-soft-water"
      ref={sectionRef}
      // Added 'anchor-target' class here
      className="py-[3.072rem] anchor-target"
    >
      <FadeInSection>
        <div className="container mx-auto px-6">
          <div className="text-center mb-[2.048rem]">
            <h2 className="text-4xl font-bold text-white">{heading}</h2>
            <p className="text-gray-400 max-w-3xl mx-auto mt-4">{sub}</p>
          </div>

          {/* Soap Scum Buildup */}
          <div
            className="md:flex items-center md:gap-[1.536rem] mb-[2.56rem]"
            ref={(n) => observeBlock(n as Element, 'soap_scum')}
          >
            <div className="md:w-1/2">
              <h3 className="text-2xl font-bold mb-4">{blocks.soap_scum.title}</h3>
              <p className="text-gray-400 mb-6">{blocks.soap_scum.body}</p>
              <ImageComparisonSlider beforeImg="/soap-scum_before.png" afterImg="/soap-scum_after.png" />
            </div>
            <div className="md:w-1/2 mt-[1.536rem] md:mt-0">
              <ul className="space-y-[0.768rem] text-lg">
                {blocks.soap_scum.list.map(renderListItem)}
              </ul>
            </div>
          </div>

          {/* Dull, Stiff Laundry */}
          <div
            className="md:flex items-center md:gap-[1.536rem] flex-row-reverse mb-[2.56rem]"
            ref={(n) => observeBlock(n as Element, 'laundry')}
          >
            <div className="md:w-1/2">
              <h3 className="text-2xl font-bold mb-4">{blocks.laundry.title}</h3>
              <p className="text-gray-400 mb-6">{blocks.laundry.body}</p>
              <ImageComparisonSlider beforeImg="/laundry_dull.png" afterImg="/laundry_soft.png" />
            </div>
            <div className="md:w-1/2 mt-[1.536rem] md:mt-0">
              <ul className="space-y-[0.768rem] text-lg">
                {blocks.laundry.list.map(renderListItem)}
              </ul>
            </div>
          </div>

          {/* Dry Skin & Hair */}
          <div
            className="md:flex items-center md:gap-[1.536rem] mb-[2.56rem]"
            ref={(n) => observeBlock(n as Element, 'skin_hair')}
          >
            <div className="md:w-1/2">
              <h3 className="text-2xl font-bold mb-4">{blocks.skin_hair.title}</h3>
              <p className="text-gray-400 mb-6">{blocks.skin_hair.body}</p>
              <ImageComparisonSlider beforeImg="/skin_hair_dry.png" afterImg="/skin_hair_soft.png" />
            </div>
            <div className="md:w-1/2 mt-[1.536rem] md:mt-0">
              <ul className="space-y-[0.768rem] text-lg">
                {blocks.skin_hair.list.map(renderListItem)}
              </ul>
            </div>
          </div>

          {/* Appliance Damage */}
          <div
            className="md:flex items-center md:gap-[1.536rem] flex-row-reverse"
            ref={(n) => observeBlock(n as Element, 'appliances')}
          >
            <div className="md:w-1fs-full/2">
              <h3 className="text-2xl font-bold mb-4">{blocks.appliances.title}</h3>
              <p className="text-gray-400 mb-6">{blocks.appliances.body}</p>
              <ImageComparisonSlider beforeImg="/appliance_scale.png" afterImg="/appliance_clean.png" />
            </div>
            <div className="md:w-1/2 mt-[1.536rem] md:mt-0">
              <ul className="space-y-[0.768rem] text-lg">
                {blocks.appliances.list.map(renderListItem)}
              </ul>
            </div>
          </div>
        </div>
      </FadeInSection>
    </section>
  );
};

export default WhySoftWater;
